/**
 * Reusable AdvancedMarkerElement object pool.
 *
 * Why this file exists:
 * - Creating and destroying marker instances repeatedly causes avoidable allocations and GC pressure.
 * - Pooling keeps interaction smooth when marker sets change frequently.
 *
 * Responsibilities:
 * - Acquire an existing record when possible.
 * - Create a new marker record only when needed.
 * - Reset and recycle records on release.
 * - Hard-cap pool size to avoid unbounded memory growth.
 */

export default function createMarkerPool({
  getMarkerLibrary,
  createMarkerElement,
  maxPoolSize,
  onRecordClick,
  resetRecord,
}) {
  const markerPool = [];
  const activeRecords = new Set();

  function createRecord(element, markerLibrary) {
    const record = {
      marker: new markerLibrary.AdvancedMarkerElement({
        content: element,
        gmpClickable: true,
      }),
      markerData: null,
      element,
      mode: null,
      lastPrice: '',
      clickListener: null,
      spatialKey: null,
    };

    // Attach the click listener once for the full lifetime of this record.
    // The callback reads record.markerData at call time so it always gets
    // current data regardless of how many times the record is reused.
    // Attaching here (rather than in acquire) eliminates addEventListener /
    // removeEventListener churn on every pool cycle.
    // gmp-click is a DOM CustomEvent — store the handler for removeEventListener.
    record.clickListener = (event) => {
      onRecordClick?.(record, event);
    };
    record.marker.addEventListener('gmp-click', record.clickListener);

    return record;
  }

  function detachRecord(record) {
    if (!record) {
      return;
    }

    // Only detach from the map — the click listener is kept alive for the
    // full record lifetime and is only removed in destroyRecord.
    record.marker.map = null;
  }

  function destroyRecord(record) {
    if (!record) {
      return;
    }

    record.marker.removeEventListener('gmp-click', record.clickListener);
    record.marker.map = null;
    record.element?.remove?.();
  }

  function acquire() {
    const markerLibrary = getMarkerLibrary?.();
    if (!markerLibrary) {
      return null;
    }

    // Reuse a pooled record — click listener and element are already in place.
    const reused = markerPool.pop();
    if (reused) {
      activeRecords.add(reused);
      return reused;
    }

    const element = createMarkerElement();
    const record = createRecord(element, markerLibrary);
    activeRecords.add(record);
    return record;
  }

  function release(record) {
    if (!record) {
      return;
    }

    activeRecords.delete(record);
    resetRecord?.(record);
    detachRecord(record);

    if (markerPool.length >= maxPoolSize) {
      destroyRecord(record);
      return;
    }

    markerPool.push(record);
  }

  function drain() {
    markerPool.forEach((record) => {
      destroyRecord(record);
    });
    markerPool.length = 0;

    activeRecords.forEach((record) => {
      destroyRecord(record);
    });
    activeRecords.clear();
  }

  return {
    acquire,
    release,
    drain,
  };
}